/**
 * Custom Pack building blocks shared by the Catalog, Purchase and Embed
 * operations. Inlined by the importing paths (like `oddsLevelSchema` in
 * components.ts), not registered under `schemas`.
 */

/** Why a lookup of a custom pack game answers `404` — the tail of every such `404`. */
export const CUSTOM_PACK_UNAVAILABLE_REASONS =
  'it is not offered to your platform, or your platform requires provably fair (custom packs are not provably ' +
  'fair)';

/** What the calls that read the RTP (Get a game, quote, purchase) add; the card search never reads it. */
export const CUSTOM_PACK_UNPRICED_NOTE =
  'The same answer when the custom pack cannot be priced: its RTP setting is missing, not positive or above the ' +
  'maximum RTP allowed for custom packs.';

/** `game_kind` of the game an order embeds; the rules and the odds stay on **Get a game**. */
export const orderGameKind = {
  type: 'string',
  enum: ['loot_pack', 'custom_pack'],
  description:
    'The kind of game, as in **Get a game**. The game of a `custom_pack` order carries no `custom_pack` rules and none ' +
    'of the odds keys (`odds`, `odds_levels`, `big_win_chance`, `min_value`, `max_value`, `estimated_value`, ' +
    '`max_for_purchase_count`).',
};

const PROBLEM_CODES = [
  'SELECTION_MALFORMED',
  'TOO_FEW_CARDS',
  'TOO_MANY_CARDS',
  'DUPLICATE_CARD',
  'CHANCE_OUT_OF_RANGE',
  'SUM_NOT_1000',
  'CARD_UNAVAILABLE',
];

/** The picked cards: `custom_pack.items` of the quote and purchase requests. */
export const customPackSelectionItems = {
  type: 'array',
  description:
    'The picked cards, each with the chance that it is the one drawn. The number of cards and every chance must ' +
    'stay inside `custom_pack.limits` from **Get a game**, a card may be listed once, and the chances must add up ' +
    'to exactly 1000.',
  items: {
    type: 'object',
    title: 'CustomPackSelectionItem',
    required: ['pool_item_id', 'chance_permille'],
    properties: {
      pool_item_id: {
        type: 'integer',
        description: 'The card — an `id` from **Search cards for a custom pack**.',
        example: 11,
      },
      chance_permille: {
        type: 'integer',
        description: 'Chance that this card is the one drawn, in permille: a whole number where 1 is 0.1% and 1000 is 100%.',
        example: 200,
      },
    },
  },
  example: [
    { pool_item_id: 11, chance_permille: 200 },
    { pool_item_id: 12, chance_permille: 200 },
    { pool_item_id: 13, chance_permille: 200 },
    { pool_item_id: 14, chance_permille: 200 },
    { pool_item_id: 15, chance_permille: 200 },
  ],
};

/** What a selection costs right now; also the body of a price change. */
export const customPackQuoteSchema = {
  type: 'object',
  title: 'CustomPackQuote',
  required: ['price_cents', 'ev_cents', 'rtp_ppm', 'items'],
  properties: {
    price_cents: {
      type: 'integer',
      description:
        'What the selection costs, in cents — the amount the wallet is charged. The expected value divided by the ' +
        'RTP, rounded up to the cent.',
      example: 32667,
    },
    ev_cents: {
      type: 'integer',
      description: 'Expected value of the selection, in cents: the chance-weighted average value of the card drawn.',
      example: 29400,
    },
    rtp_ppm: {
      type: 'integer',
      description: 'The RTP the price is based on, in parts per million (900000 is 90%).',
      example: 900000,
    },
    items: {
      type: 'array',
      description: 'The selection as priced: every card with its chance and its current fair market value.',
      items: {
        type: 'object',
        title: 'CustomPackQuoteItem',
        required: ['pool_item_id', 'chance_permille', 'fair_market_value_cents'],
        properties: {
          pool_item_id: { type: 'integer', description: 'The card.' },
          chance_permille: { type: 'integer', description: 'Its chance, in permille, as sent.' },
          fair_market_value_cents: {
            type: 'integer',
            description: 'Fair market value of the card used for the price, in cents of USD.',
          },
        },
      },
    },
  },
};

export const customPackQuoteExample = {
  price_cents: 32667,
  ev_cents: 29400,
  rtp_ppm: 900000,
  items: [
    { pool_item_id: 11, chance_permille: 200, fair_market_value_cents: 100000 },
    { pool_item_id: 12, chance_permille: 200, fair_market_value_cents: 30000 },
    { pool_item_id: 13, chance_permille: 200, fair_market_value_cents: 10000 },
    { pool_item_id: 14, chance_permille: 200, fair_market_value_cents: 5000 },
    { pool_item_id: 15, chance_permille: 200, fair_market_value_cents: 2000 },
  ],
};

/** Error body of the custom pack calls; `details`, `quote` and `pool_order_id` appear only with the codes noted. */
export const customPackErrorSchema = {
  type: 'object',
  title: 'CustomPackError',
  required: ['error', 'error_detail'],
  properties: {
    error: { type: 'integer', enum: [1], description: 'Non-zero marks a failure.' },
    error_detail: {
      type: 'object',
      required: ['code', 'message'],
      properties: {
        code: { type: 'string', description: 'Machine-readable error code.', example: 'CUSTOM_PACK_INVALID_SELECTION' },
        message: { type: 'string', description: 'Human-readable explanation.' },
        details: {
          type: 'array',
          description:
            'The problems found with the selection. Present with `CUSTOM_PACK_INVALID_SELECTION` and with ' +
            '`CARD_UNAVAILABLE`; absent from other errors.',
          items: {
            type: 'object',
            title: 'CustomPackSelectionProblem',
            required: ['code'],
            properties: {
              code: {
                type: 'string',
                enum: PROBLEM_CODES,
                description: [
                  '| code | meaning |',
                  '|------|---------|',
                  '| `SELECTION_MALFORMED` | `custom_pack.items` is missing, is not a list, or holds an entry without whole-number `pool_item_id` and `chance_permille`. |',
                  '| `TOO_FEW_CARDS` | Fewer cards than `limits.min_cards`. |',
                  '| `TOO_MANY_CARDS` | More cards than `limits.max_cards`. |',
                  '| `DUPLICATE_CARD` | The card is listed more than once. |',
                  '| `CHANCE_OUT_OF_RANGE` | The card’s chance is outside `limits.min_chance_permille` – `limits.max_chance_permille`. |',
                  '| `SUM_NOT_1000` | The chances do not add up to exactly 1000. |',
                  '| `CARD_UNAVAILABLE` | The card can no longer be picked — for example another player just won it. |',
                ].join('\n'),
              },
              pool_item_id: {
                type: 'integer',
                nullable: true,
                description: 'The card the problem is about. Null when it concerns the selection as a whole.',
              },
            },
          },
        },
      },
    },
    quote: {
      ...customPackQuoteSchema,
      description: 'Present with `CUSTOM_PACK_PRICE_CHANGED` only: the selection priced right now.',
    },
    pool_order_id: {
      type: 'integer',
      nullable: true,
      description:
        'Present with `IDEMPOTENCY_KEY_REUSED` only: the id of the order the key first bought. Null when there is ' +
        'none, or it is not the player’s.',
    },
  },
};

/** `422 CUSTOM_PACK_INVALID_SELECTION` for a card that was taken — the same body from a quote and from a purchase. */
export const customPackInvalidSelectionExample = {
  error: 1,
  error_detail: {
    code: 'CUSTOM_PACK_INVALID_SELECTION',
    message: 'Invalid custom pack selection',
    details: [{ code: 'CARD_UNAVAILABLE', pool_item_id: 11 }],
  },
};
